import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import AuthService from '../services/authService.js';

// Initialize auth service
const authService = new AuthService();

// Hardcoded user credentials for demo
const USERS = [
  { id: 'user1', user: 'user1', email: 'user1@example.com', password: 'pass1' },
  { id: 'user2', user: 'user2', email: 'user2@example.com', password: 'pass2' }
];

export const useAuthStore = create(
  persist(
    (set, get) => ({
      currentUser: null,
      isAuthenticated: false,
      currentDeviceId: null,

      login: async (email, password, recaptchaToken) => {
        const response = await authService.signin(email, password, recaptchaToken);
        console.log('🔍 Login Response Debug:', response);
        console.log('🔐 Device ID in response:', response.deviceId);

        try {
          if (response && response.success && response.data?.statusCode === 200) {
            const apiData = response.data.data;
            const userData = {
              id: apiData?.userId,
              email: apiData?.userEmailId || email,
              token: apiData?.token,
              is_profile_set: apiData?.is_profile_set,
              lastLogoutTime: apiData?.lastLogoutTime,
              ...apiData
            };

            console.log('✅ Login successful, setting user data:', userData);
            console.log('🔍 is_profile_set from API:', apiData?.is_profile_set, typeof apiData?.is_profile_set);

            if (response.deviceId) {
              localStorage.setItem('deviceId', response.deviceId);
              console.log('🔐 Persisted device ID to localStorage:', response.deviceId);
            } else {
              console.warn('⚠️ No deviceId in login response');
            }

            set({
              currentUser: userData,
              isAuthenticated: true,
              currentDeviceId: response.deviceId || null
            });

            console.log('🔐 Stored device ID for session:', response.deviceId);
            console.log('🔍 Auth store state after login:', {
              hasUser: !!userData,
              hasDeviceId: !!response.deviceId,
              deviceId: response.deviceId,
              userEmail: userData?.email
            });

            return {
              success: true,
              is_profile_set: apiData?.is_profile_set
            };
          }

          console.log('❌ Login failed - invalid response structure');
          return { success: false, error: response?.error || 'Invalid response' };
        } catch (error) {
          console.error('Login processing failed:', error);
          return { success: false, error: error.message };
        }

        if (!response.success) {
          console.log('🔄 API call unsuccessful, checking fallback users');
          const user = USERS.find(
            (u) => u.email.toLowerCase() === email.toLowerCase() && u.password === password
          );

          if (user) {
            console.log('🔄 Using fallback hardcoded user');
            const deviceIdFromApiCall = response.deviceId;

            if (deviceIdFromApiCall) {
              console.log('🔐 Using device ID from API call for fallback user:', deviceIdFromApiCall);
              localStorage.setItem('deviceId', deviceIdFromApiCall);
            } else {
              console.warn('⚠️ No device ID in API response for fallback user');
            }

            set({
              currentUser: user,
              isAuthenticated: true,
              currentDeviceId: deviceIdFromApiCall
            });

            return { success: true, is_profile_set: true };
          }
        }

        return { success: false, error: response?.error || 'Login failed' };
      },

      thirdPartyLogin: async (firebaseUser) => {
        try {
          console.log('🔍 Firebase user data:', firebaseUser);

          const { extractSocialLoginData, getDeviceType, getClientIP, getClientLocation } = await import('../utils/helpers.js');
          const socialData = extractSocialLoginData(firebaseUser);
          console.log('🔍 Extracted social data:', socialData);

          const device = getDeviceType();
          const ipAddress = await getClientIP();
          const location = await getClientLocation();

          // Generate deviceId here to ensure consistency
          const { generateDeviceIdWithTimestamp } = await import('../utils/deviceHelper.js');
          const deviceId = generateDeviceIdWithTimestamp();
          console.log('🔐 Generated deviceId for social login:', deviceId);

          const response = await authService.socialLogin(
            socialData.email,
            socialData.providerName,
            socialData.socialUserId,
            device,
            ipAddress,
            location,
            null, // fcmToken
            deviceId // Pass the generated deviceId
          );

          console.log('🔍 Social Login API Response:', response);
          console.log('🔍 Response validation:', {
            hasResponse: !!response,
            hasSuccess: response?.success,
            hasData: !!response?.data,
            statusCode: response?.data?.statusCode,
            isProfileSet: response?.data?.data?.is_profile_set,
            deviceId: response.deviceId
          });

          if (response && response.success && response.data?.statusCode === 200) {
            console.log('✅ API call successful, processing response...');
            const apiData = response.data.data;
            console.log('🔍 API Data:', apiData);
            console.log('🔍 is_profile_set from API:', apiData?.is_profile_set, typeof apiData?.is_profile_set);

            const userData = {
              id: apiData?.userId,
              email: apiData?.userEmailId || socialData.email,
              token: apiData?.token,
              is_profile_set: apiData?.is_profile_set,
              displayName: firebaseUser.displayName || firebaseUser.providerData?.[0]?.displayName,
              photoURL: firebaseUser.photoURL || firebaseUser.providerData?.[0]?.photoURL,
              providerData: firebaseUser.providerData,
              ...apiData
            };

            if (response.deviceId) {
              localStorage.setItem('deviceId', response.deviceId);
              console.log('🔐 Persisted deviceId to localStorage:', response.deviceId);
            } else {
              console.warn('⚠️ No deviceId in social login response, using generated deviceId:', deviceId);
              localStorage.setItem('deviceId', deviceId);
            }

            set({
              currentUser: userData,
              isAuthenticated: true,
              currentDeviceId: response.deviceId || deviceId // Use response.deviceId or fallback to generated
            });

            console.log('✅ Social login successful, setting user data:', userData);
            console.log('🔐 Stored deviceId for social login session:', response.deviceId || deviceId);

            const returnValue = {
              success: true,
              is_profile_set: apiData?.is_profile_set
            };
            console.log('🔍 Returning to LoginPage:', returnValue);
            return returnValue;
          }

          console.log('❌ Social login failed - invalid response structure');
          return { success: false, error: response?.error || 'Invalid response' };
        } catch (error) {
          console.error('❌ Social login API call failed:', error);
          console.log('🔄 Using fallback Firebase-only authentication');
          set({
            currentUser: {
              id: firebaseUser.uid,
              email: firebaseUser.email || firebaseUser.providerData?.[0]?.email,
              displayName: firebaseUser.displayName || firebaseUser.providerData?.[0]?.displayName,
              photoURL: firebaseUser.photoURL || firebaseUser.providerData?.[0]?.photoURL,
              providerData: firebaseUser.providerData,
              is_profile_set: true
            },
            isAuthenticated: true,
            currentDeviceId: null // Avoid setting deviceId for fallback
          });
          return { success: true, is_profile_set: true };
        }
      },

      logout: async () => {
        console.log('🔄 Starting API-first logout process');
        const state = get();
        const userEmail = state.currentUser?.email;
        let deviceId = state.currentDeviceId;

        console.log('🔍 Logout state check:', {
          hasUser: !!state.currentUser,
          userEmail,
          hasDeviceId: !!deviceId,
          deviceId,
          fullState: {
            isAuthenticated: state.isAuthenticated,
            currentDeviceId: state.currentDeviceId
          }
        });

        if (!userEmail) {
          console.error('❌ Cannot logout: No user email found');
          throw new Error('Cannot logout: User email not available');
        }

        if (!deviceId) {
          console.warn('⚠️ No stored device ID in state, checking localStorage');
          deviceId = localStorage.getItem('deviceId');
          if (!deviceId) {
            console.error('❌ No device ID found in state or localStorage');
            throw new Error('Cannot logout: Device ID not available');
          }
          console.log('🔐 Retrieved device ID from localStorage:', deviceId);
          set((state) => ({ ...state, currentDeviceId: deviceId }));
        }

        try {
          console.log('📧 Calling signout API with device ID:', { userEmail, deviceId });
          const result = await authService.signout(userEmail, deviceId);

          if (!result.success) {
            console.warn('⚠️ Signout API returned failure — will still clear local state:', result.error);
          } else {
            console.log('✅ Signout API succeeded');
          }
        } catch (error) {
          // API call failed (e.g. 401, network error) — log and continue to cleanup
          console.warn('⚠️ Signout API call failed — will still clear local state:', error.message);
        } finally {
          // Always clear local auth state regardless of API outcome
          const { clearAuthStorage } = await import('../utils/authValidation.js');
          clearAuthStorage(true, true);

          await authService.apiClient.setAuthToken(null);
          localStorage.removeItem('deviceId');

          set({ currentUser: null, isAuthenticated: false, currentDeviceId: null });
          console.log('✅ Local auth state cleared');
        }

        return { success: true };
      },

      setAuthenticationState: async (userData) => {
        console.log('🔐 Setting authentication state for traditional signup:', userData);
        let deviceId = userData.deviceId;
        if (!deviceId) {
          const { generateDeviceIdWithTimestamp } = await import('../utils/deviceHelper.js');
          deviceId = generateDeviceIdWithTimestamp();
          console.log('🔐 Generated device ID for traditional signup:', deviceId);
        }
        localStorage.setItem('deviceId', deviceId);
        set({
          currentUser: userData,
          isAuthenticated: true,
          currentDeviceId: deviceId
        });
      },

      updateProfileStatus: (isProfileSet) => {
        console.log('🔄 Updating profile completion status:', isProfileSet);
        set((state) => ({
          currentUser: state.currentUser ? {
            ...state.currentUser,
            is_profile_set: isProfileSet
          } : null
        }));
      },

      isProfileComplete: () => {
        const state = get();
        return state.currentUser?.is_profile_set === true;
      }
    }),
    { name: 'auth-storage' }
  )
);